import { memo, useState } from 'react';
import { PreviewModes } from '@vp/react-product-preview';
import { FlexBox, SquareImageContainer, useScreenClass } from '@vp/swan';
import { ViewLargerImagesModal } from './ViewLargerImagesModal';
import { ProductImagePreview } from './ProductImagePreview';
import { useLineItemData } from '../../contexts/LineItemDataContext';

interface ItemPreviewDisplayProps {
  useSquareDimensionsForPurcs?: boolean;
}

/**
 * Responsible for rendering image preview and "view larger" icon /modal. Component renders purcsData when available and falls back to livePreviewUrl.
 */
export const ItemPreviewDisplay = memo(
  ({ useSquareDimensionsForPurcs }: ItemPreviewDisplayProps) => {
    const {
      documentUrl: documentRefUrl,
      livePreviewUrl,
      attributes: productAttributeList,
      productKey,
    } = useLineItemData();

    const screenClass = useScreenClass();
    const [isViewLargerModalOpen, setIsViewLargerModalOpen] = useState(false);

    // PURCs required params: https://gitlab.com/vistaprint-org/design-technology/scenes/purcs-react#required-parameters
    const hasRequiredPurcsData = !!(
      productKey &&
      productAttributeList &&
      documentRefUrl
    );

    // if we don't have the required PURCS data and no livePreviewUrl, we can't render anything
    if (!hasRequiredPurcsData && !livePreviewUrl) {
      return null;
    }

    const isMobile = screenClass === 'xs' || screenClass === 'sm';

    // Standardizes padding between magnifying glass and image when rendering static image vs. PurcsPreview
    const magnifyingGlassPadding = !hasRequiredPurcsData && !isMobile ? 7 : 0;

    if (useSquareDimensionsForPurcs) {
      return (
        <>
          <SquareImageContainer data-testid="square-image-container">
            <ProductImagePreview
              previewMode={PreviewModes.FirstPageOnly}
              onClick={() => setIsViewLargerModalOpen(true)}
            />
          </SquareImageContainer>
          <FlexBox alignItems="center" flexDirection="column" pt={3}>
            <ViewLargerImagesModal
              isOpen={isViewLargerModalOpen}
              onZoomButtonClick={() => setIsViewLargerModalOpen(true)}
              onDismiss={() => setIsViewLargerModalOpen(false)}
            >
              <ProductImagePreview previewMode={PreviewModes.Carousel} />
            </ViewLargerImagesModal>
          </FlexBox>
        </>
      );
    }

    return (
      <>
        <ProductImagePreview
          previewMode={
            isMobile ? PreviewModes.FirstPageOnly : PreviewModes.Carousel
          }
          onClick={() => setIsViewLargerModalOpen(true)}
        />
        <FlexBox
          alignItems="center"
          flexDirection="column"
          pt={magnifyingGlassPadding}
        >
          <ViewLargerImagesModal
            isOpen={isViewLargerModalOpen}
            onZoomButtonClick={() => setIsViewLargerModalOpen(true)}
            onDismiss={() => setIsViewLargerModalOpen(false)}
          >
            <ProductImagePreview previewMode={PreviewModes.Carousel} />
          </ViewLargerImagesModal>
        </FlexBox>
      </>
    );
  }
);
ItemPreviewDisplay.displayName = 'ItemPreviewDisplay';
